import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, LogOut, Lock, Plus, Eye, EyeOff, Trash2, Save, X,
  FileText, RefreshCw, ArrowLeft, PenSquare, Clock, CalendarClock,
  Sparkles, SearchCheck, Calendar, ChevronRight, Share2, Wand2, Check,
  ChevronUp, ChevronDown, ImagePlus, SpellCheck2, UserRound, Undo2, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { renderArticleContent } from '../utils/articleRenderer.jsx';
import { clipboardToEditorMarkdown } from '../utils/pasteToMarkdown';
import { BlogImagePicker } from '../components/BlogImagePicker';
import {
  AiScoreBadge, HumanizeBatchBar, HumanizeBatchItemBadge, HumanizeReviewDialog, useHumanizeBatch,
} from '../components/HumanizeBatch';
import { StyleCheckPanel, StyleScoreBadge, useStyleSummaries } from '../components/StyleCheck';
import { adminStyleCheck } from '../services/api';
import { BLOG_IMAGE_GALLERY, BLOG_IMAGE_CATEGORIES } from '../config/blogImages';
import {
  adminListBlogPosts, adminGetBlogPost, adminUpdateBlogPost,
  adminCreateBlogPost, adminDeleteBlogPost, adminReorderBlogPosts,
  adminAiTitleSuggestions, adminAiSeoCheck, adminAiCleanup, adminAiProofread,
  adminAiHumanizeJob, adminHumanizeApply, adminHumanizeRevert,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const fmtHuDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// -------------------------------------------------------------------------
// Login screen
// -------------------------------------------------------------------------
const LoginScreen = ({ onAuth }) => {
  const [key, setKey] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setSubmitting(true);
    try {
      await adminListBlogPosts(key.trim());
      localStorage.setItem(ADMIN_KEY_STORAGE, key.trim());
      onAuth(key.trim());
      toast.success('Bejelentkezve.');
    } catch (_err) {
      toast.error('Érvénytelen admin kulcs.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-blog-login-form"
        className="w-full max-w-md bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 border border-gray-700 shadow-2xl"
      >
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <PenSquare className="text-yellow-500" size={28} />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Blog Szerkesztő
        </h1>
        <p className="text-sm text-gray-400 text-center mb-6">
          Cikkek szerkesztése, létrehozása, elrejtése és törlése.
        </p>
        <Input
          data-testid="admin-blog-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="ADMIN_API_KEY"
          className="bg-gray-900 border-gray-700 text-white mb-4"
        />
        <Button
          data-testid="admin-blog-login-btn"
          type="submit"
          disabled={submitting || !key.trim()}
          className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
        >
          {submitting ? <><Loader2 className="mr-2 animate-spin" size={16} /> Belépés…</> : <><Lock className="mr-2" size={16} /> Belépés</>}
        </Button>
      </form>
    </div>
  );
};

// -------------------------------------------------------------------------
// Post list
// -------------------------------------------------------------------------
const PostList = ({ posts, loading, onSelect, onHumanize, onCreate, onRefresh, onLogout, onMove, reordering, batch, worstSlugs, styleRows }) => (
  <div className="min-h-screen bg-gray-950 text-white">
    <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
      <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
            <FileText className="text-yellow-500" size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Blog Szerkesztő
            </h1>
            <p className="text-xs text-gray-400">{posts.length} cikk az adatbázisban</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-blog-refresh-btn"
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            className="text-gray-300 hover:text-white"
          >
            <RefreshCw size={14} className="mr-1.5" />
            Frissítés
          </Button>
          <Button
            data-testid="admin-blog-new-btn"
            onClick={onCreate}
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
          >
            <Plus size={16} className="mr-1.5" />
            Új cikk
          </Button>
          <Button
            data-testid="admin-blog-logout-btn"
            variant="ghost"
            size="sm"
            onClick={onLogout}
            className="text-gray-400 hover:text-red-400"
          >
            <LogOut size={14} />
          </Button>
        </div>
      </div>
    </header>

    <main className="container mx-auto px-6 py-8">
      {loading ? (
        <div className="text-center py-24">
          <Loader2 className="animate-spin mx-auto mb-3 text-yellow-500" size={28} />
          <p className="text-gray-400">Cikkek betöltése…</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-24 text-gray-400">
          Nincs cikk. Kattints az &quot;Új cikk&quot; gombra a létrehozáshoz.
        </div>
      ) : (
        <div className="space-y-2">
          <HumanizeBatchBar batch={batch} posts={posts} />
          {posts.map((p, i) => (
            <div
              key={p.id}
              data-testid={`admin-blog-item-${p.slug}`}
              className="flex items-stretch gap-2 rounded-xl border border-gray-800 bg-gray-900/60 hover:bg-gray-800/80 hover:border-yellow-600/40 transition-all p-4 md:p-5"
            >
              <div className="flex flex-col justify-center gap-1 shrink-0">
                <button
                  data-testid={`admin-blog-move-up-${p.slug}`}
                  disabled={i === 0 || reordering}
                  onClick={() => onMove(p.id, -1)}
                  title="Feljebb"
                  className="w-8 h-8 rounded-md border border-gray-700 bg-gray-900 text-gray-300 hover:border-yellow-500 hover:text-yellow-400 disabled:opacity-25 disabled:hover:border-gray-700 disabled:hover:text-gray-300 flex items-center justify-center transition-colors"
                >
                  <ChevronUp size={16} />
                </button>
                <button
                  data-testid={`admin-blog-move-down-${p.slug}`}
                  disabled={i === posts.length - 1 || reordering}
                  onClick={() => onMove(p.id, 1)}
                  title="Lejjebb"
                  className="w-8 h-8 rounded-md border border-gray-700 bg-gray-900 text-gray-300 hover:border-yellow-500 hover:text-yellow-400 disabled:opacity-25 disabled:hover:border-gray-700 disabled:hover:text-gray-300 flex items-center justify-center transition-colors"
                >
                  <ChevronDown size={16} />
                </button>
              </div>
              <button
                data-testid={`admin-blog-open-${p.slug}`}
                onClick={() => onSelect(p.id)}
                className="flex-1 min-w-0 text-left"
              >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-800 rounded px-1.5 py-0.5">#{i + 1}</span>
                    {(() => {
                      const s = p.status || (p.published ? 'published' : (p.scheduled_at ? 'scheduled' : 'draft'));
                      const meta = STATUS_META[s] || STATUS_META.draft;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${meta.color}`}>
                          {s === 'published' ? <Eye size={10} /> : s === 'scheduled' ? <Clock size={10} /> : <EyeOff size={10} />}
                          {meta.label}
                        </span>
                      );
                    })()}
                    {p.manually_edited && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-400 bg-yellow-500/10 px-2 py-0.5 rounded">
                        Kézi
                      </span>
                    )}
                    {p.humanized_at && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded">
                        Emberi hang
                      </span>
                    )}
                    <AiScoreBadge
                      score={p.ai_score}
                      level={p.ai_level}
                      testId={`admin-blog-ai-score-${p.slug}`}
                    />
                    {styleRows?.[p.slug] && (
                      <StyleScoreBadge
                        score={styleRows[p.slug].style_score}
                        band={styleRows[p.slug].band}
                        red={styleRows[p.slug].red}
                        yellow={styleRows[p.slug].yellow}
                        testId={`admin-blog-style-score-${p.slug}`}
                      />
                    )}
                    {worstSlugs.includes(p.slug) && (
                      <span
                        data-testid={`admin-blog-worth-rewrite-${p.slug}`}
                        className="text-[10px] font-bold uppercase tracking-widest text-orange-300 bg-orange-500/10 px-2 py-0.5 rounded"
                      >
                        Érdemes átírni
                      </span>
                    )}
                    <HumanizeBatchItemBadge job={batch.job} postId={p.id} />
                    <span className="text-[10px] text-gray-500">{p.category}</span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-white leading-snug" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {p.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 truncate">/{p.slug}</p>
                  {p.scheduled_at && !p.published && (
                    <p className="text-[11px] text-sky-300 mt-1">
                      Publikálás: {fmtHuDate(p.scheduled_at)}
                    </p>
                  )}
                </div>
                <div className="text-right text-xs text-gray-500 shrink-0">
                  <div>{fmtHuDate(p.date)}</div>
                  {p.updated_at && <div className="mt-1">frissítve: {fmtHuDate(p.updated_at)}</div>}
                </div>
              </div>
              </button>
              <div className="flex items-center shrink-0 pl-1">
                <button
                  data-testid={`admin-blog-humanize-${p.slug}`}
                  onClick={() => onHumanize(p.id)}
                  title="Emberibbé tétel — AI-fordulatok kiszedése, előnézettel"
                  className="w-9 h-9 rounded-md border border-gray-700 bg-gray-900 text-emerald-300 hover:border-emerald-500 hover:text-emerald-200 flex items-center justify-center transition-colors"
                >
                  <Zap size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
    <HumanizeReviewDialog adminKey={batch.adminKey} batch={batch} />
  </div>
);

// -------------------------------------------------------------------------
// Post editor
// -------------------------------------------------------------------------
const emptyPost = () => ({
  id: null,
  slug: '',
  title: '',
  category: 'Edzéselmélet és Tudomány',
  excerpt: '',
  content: '',
  image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&h=750&fit=crop',
  read_time: '5 perc olvasás',
  published: false,
  scheduled_at: null,
});

// ISO date <-> <input type="datetime-local"> value (which uses local time, no TZ).
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localInputToIso = (val) => {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const deriveStatus = (post) => {
  if (post.published) return 'published';
  if (post.scheduled_at) return 'scheduled';
  return 'draft';
};

const STATUS_META = {
  draft:     { label: 'Piszkozat',  color: 'bg-gray-600/20 text-gray-300' },
  scheduled: { label: 'Ütemezett',  color: 'bg-sky-500/15 text-sky-300' },
  published: { label: 'Publikálva', color: 'bg-emerald-500/15 text-emerald-300' },
};

const DEFAULT_CATEGORIES = [
  'Edzéselmélet és Tudomány',
  'Táplálkozás',
  'Mérés és Diagnosztika',
  'Testtartás',
  'Esztétika és Edzéselmélet',
];

const calcReadTime = (content) => {
  const words = (content || '').trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} perc olvasás`;
};

const PostEditor = ({ adminKey, initialPost, categories = [], usedImages = [], autoHumanize = false, onBack, onSaved, onDeleted }) => {
  const isNew = !initialPost;
  const [draft, setDraft] = useState(initialPost || emptyPost());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [status, setStatus] = useState(() => deriveStatus(initialPost || emptyPost()));
  const [scheduleLocal, setScheduleLocal] = useState(() => isoToLocalInput(initialPost?.scheduled_at));
  const [aiTitles, setAiTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [seoResult, setSeoResult] = useState(null);
  const [loadingSeo, setLoadingSeo] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [loadingCleanup, setLoadingCleanup] = useState(false);
  const [proofreadResult, setProofreadResult] = useState(null);
  const [loadingProofread, setLoadingProofread] = useState(false);
  const [humanizeResult, setHumanizeResult] = useState(null);
  const [loadingHumanize, setLoadingHumanize] = useState(false);
  const [applyingHumanize, setApplyingHumanize] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [hasBackup, setHasBackup] = useState(Boolean(initialPost?.has_humanize_backup));
  const [inlineImagePickerOpen, setInlineImagePickerOpen] = useState(false);
  const [inlineCat, setInlineCat] = useState('Mind');
  const contentTextareaRef = useRef(null);
  const contentSelectionRef = useRef({ start: 0, end: 0 });
  const [readTimeTouched, setReadTimeTouched] = useState(false);
  const [newCategoryMode, setNewCategoryMode] = useState(false);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // Auto read time: recompute whenever content changes unless manually overridden.
  const setContent = (value) => {
    set(readTimeTouched ? { content: value } : { content: value, read_time: calcReadTime(value) });
  };

  const categoryOptions = useMemo(() => {
    const all = [...categories, ...DEFAULT_CATEGORIES];
    if (draft.category && !all.includes(draft.category)) all.push(draft.category);
    return [...new Set(all.filter(Boolean))];
  }, [categories, draft.category]);

  const handleGenTitles = async () => {
    if (!draft.title.trim() && !draft.content.trim()) {
      toast.error('Adj meg legalább címet vagy tartalmat a javaslatokhoz.');
      return;
    }
    setLoadingTitles(true);
    try {
      const { titles } = await adminAiTitleSuggestions(adminKey, {
        title: draft.title, excerpt: draft.excerpt, content: draft.content, category: draft.category,
      });
      setAiTitles(titles || []);
      toast.success('Címjavaslatok elkészültek.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az AI címgenerálás nem sikerült.');
    } finally {
      setLoadingTitles(false);
    }
  };

  const handleSeoCheck = async () => {
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error('SEO ellenőrzéshez cím és tartalom szükséges.');
      return;
    }
    setLoadingSeo(true);
    try {
      const result = await adminAiSeoCheck(adminKey, {
        title: draft.title, excerpt: draft.excerpt, content: draft.content, category: draft.category,
      });
      setSeoResult(result);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az SEO ellenőrzés nem sikerült.');
    } finally {
      setLoadingSeo(false);
    }
  };

  const canSave = draft.title.trim() && draft.excerpt.trim() && draft.content.trim() && draft.category.trim() && draft.image.trim();

  // Build the {published, scheduled_at} pair the API expects based on the
  // currently-selected status radio and datetime picker.
  const resolvePublishFields = () => {
    if (status === 'draft')     return { published: false, scheduled_at: null };
    if (status === 'scheduled') {
      const iso = localInputToIso(scheduleLocal);
      if (!iso) throw new Error('Ütemezéshez válassz jövőbeli dátumot és időt.');
      if (new Date(iso) <= new Date()) throw new Error('Az ütemezés csak jövőbeli időpontra állítható.');
      return { published: false, scheduled_at: iso };
    }
    return { published: true, scheduled_at: null };
  };

  const handleContentPaste = (e) => {
    const converted = clipboardToEditorMarkdown(e.clipboardData);
    if (!converted) return;
    e.preventDefault();
    const el = e.target;
    const start = el.selectionStart ?? draft.content.length;
    const end = el.selectionEnd ?? start;
    const before = draft.content.slice(0, start);
    const after = draft.content.slice(end);
    // Multi-block paste: keep a blank line between existing text and the paste.
    const isBlockPaste = converted.includes('\n');
    let glueBefore = '';
    if (before.trim() && isBlockPaste && !/\n\n$/.test(before)) {
      glueBefore = before.endsWith('\n') ? '\n' : '\n\n';
    }
    let glueAfter = '';
    if (after.trim() && isBlockPaste && !/^\n\n/.test(after)) {
      glueAfter = after.startsWith('\n') ? '\n' : '\n\n';
    }
    const next = before + glueBefore + converted + glueAfter + after;
    set({ content: next });
    const pos = (before + glueBefore + converted).length;
    requestAnimationFrame(() => {
      try { el.setSelectionRange(pos, pos); } catch (_) { /* noop */ }
    });
    toast.success('Beillesztve — a szöveg szerkezete megtartva.');
  };

  const handleCleanup = async () => {
    if (!draft.content.trim()) {
      toast.error('Nincs tartalom, amit tisztítani lehetne.');
      return;
    }
    setLoadingCleanup(true);
    try {
      const { content } = await adminAiCleanup(adminKey, {
        title: draft.title, excerpt: draft.excerpt, content: draft.content, category: draft.category, slug: draft.slug,
      });
      setCleanupResult(content);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az AI tisztítás nem sikerült.');
    } finally {
      setLoadingCleanup(false);
    }
  };

  const handleProofread = async () => {
    if (!draft.content.trim()) {
      toast.error('Nincs tartalom, amit ellenőrizni lehetne.');
      return;
    }
    setLoadingProofread(true);
    try {
      const { content } = await adminAiProofread(adminKey, {
        title: draft.title, excerpt: draft.excerpt, content: draft.content, category: draft.category, slug: draft.slug,
      });
      setProofreadResult(content);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Az AI helyesírás-ellenőrzés nem sikerült.');
    } finally {
      setLoadingProofread(false);
    }
  };

  const handleHumanize = useCallback(async () => {
    if (!draft.content.trim()) {
      toast.error('Nincs tartalom, amit át lehetne írni.');
      return;
    }
    setLoadingHumanize(true);
    try {
      const { content, excerpt } = await adminAiHumanizeJob(adminKey, {
        title: draft.title, excerpt: draft.excerpt, content: draft.content, category: draft.category, slug: draft.slug,
      });
      setHumanizeResult({ content, excerpt: excerpt || draft.excerpt });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Az emberibbé tétel nem sikerült.');
    } finally {
      setLoadingHumanize(false);
    }
  }, [adminKey, draft.content, draft.excerpt, draft.title, draft.category, draft.slug]);

  // A cikklista villám-gombja egyenesen ide hoz, és azonnal lefuttatja az átírást.
  const autoRunRef = useRef(false);
  useEffect(() => {
    if (autoHumanize && !isNew && !autoRunRef.current && draft.content) {
      autoRunRef.current = true;
      handleHumanize();
    }
  }, [autoHumanize, isNew, draft.content, handleHumanize]);

  const applyHumanized = async () => {
    const result = humanizeResult;
    if (!result) return;
    if (isNew) {
      setContent(result.content);
      if (result.excerpt) set({ excerpt: result.excerpt });
      setHumanizeResult(null);
      toast.success('Szöveg lecserélve az emberibb változatra. Ne felejtsd elmenteni!');
      return;
    }
    setApplyingHumanize(true);
    try {
      const { post } = await adminHumanizeApply(adminKey, draft.id, {
        content: result.content, excerpt: result.excerpt || '',
      });
      setDraft((d) => ({ ...d, content: post.content, excerpt: post.excerpt, ai_score: post.ai_score, ai_level: post.ai_level, read_time: d.read_time }));
      setHasBackup(true);
      setHumanizeResult(null);
      toast.success('Mentve. Az eredeti változat megőrizve — bármikor visszaállítható.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally {
      setApplyingHumanize(false);
    }
  };

  const handleRevertHumanize = async () => {
    setReverting(true);
    try {
      const { post } = await adminHumanizeRevert(adminKey, draft.id);
      setDraft((d) => ({ ...d, content: post.content, excerpt: post.excerpt, ai_score: post.ai_score, ai_level: post.ai_level }));
      setHasBackup(false);
      toast.success('Az eredeti szöveg visszaállítva.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A visszaállítás nem sikerült.');
    } finally {
      setReverting(false);
    }
  };

  // Stílusellenőrző kártya művelete: odaugrás, javaslat beírása, szerkesztés.
  const applyStyleFinding = useCallback(({ mode, finding, suggestion }) => {
    const el = contentTextareaRef.current;
    const quote = finding.quote || '';
    const idx = draft.content.indexOf(quote);

    if (mode === 'locate') {
      if (idx < 0) {
        toast.info('Ez a jelzés nem egy konkrét mondathoz tartozik.');
        return;
      }
      el?.focus();
      el?.setSelectionRange(idx, idx + quote.length);
      if (el) el.scrollTop = Math.max(0, (el.scrollHeight * idx) / draft.content.length - 120);
      return;
    }

    if (!suggestion || idx < 0) {
      toast.error('Ezt a részt nem találom a szövegben — javítsd kézzel.');
      return;
    }
    const next = draft.content.slice(0, idx) + suggestion + draft.content.slice(idx + quote.length);
    setContent(next);
    if (mode === 'edit') {
      setTimeout(() => {
        el?.focus();
        el?.setSelectionRange(idx, idx + suggestion.length);
      }, 50);
      toast.success('Beírtam — most szerkesztheted. Ne felejtsd elmenteni!');
    } else {
      toast.success('Javaslat elfogadva. Ne felejtsd elmenteni!');
    }
  }, [draft.content, setContent]);

  // Track cursor position on the content textarea so "Kép beszúrás" inserts  // the markdown at the caret rather than at the end of the document.
  const rememberContentSelection = (e) => {
    const el = e.target;
    contentSelectionRef.current = {
      start: el.selectionStart ?? draft.content.length,
      end: el.selectionEnd ?? el.selectionStart ?? draft.content.length,
    };
  };

  const insertInlineImage = (url) => {
    if (!url) return;
    const el = contentTextareaRef.current;
    const { start, end } = contentSelectionRef.current;
    const value = draft.content || '';
    // Guarantee blank-line block around the image so the renderer treats it
    // as a standalone figure (see articleRenderer's `![alt](url)` branch).
    const before = value.slice(0, start);
    const after = value.slice(end);
    const needBlankBefore = before.length > 0 && !/\n\n$/.test(before);
    const needBlankAfter = after.length > 0 && !/^\n\n/.test(after);
    const glueBefore = needBlankBefore ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const glueAfter = needBlankAfter ? (after.startsWith('\n') ? '\n' : '\n\n') : '';
    const snippet = `![Kép leírása – írd át ide a képaláírást](${url})`;
    const next = before + glueBefore + snippet + glueAfter + after;
    setContent(next);
    setInlineImagePickerOpen(false);
    toast.success('Kép beszúrva. Írd át a képaláírást a szögletes zárójelben.');
    // Move caret to the alt-text placeholder so the user can immediately edit it.
    const altStart = (before + glueBefore).length + 2; // after "!["
    const altEnd = altStart + 'Kép leírása – írd át ide a képaláírást'.length;
    requestAnimationFrame(() => {
      if (el) {
        try {
          el.focus();
          el.setSelectionRange(altStart, altEnd);
        } catch (_) { /* noop */ }
      }
    });
  };

  const handleSave = async () => {
    if (!canSave) {
      toast.error('Cím, kategória, bevezető, tartalom és kép kötelező.');
      return;
    }
    let publish;
    try { publish = resolvePublishFields(); }
    catch (e) { toast.error(e.message); return; }

    setSaving(true);
    try {
      if (isNew) {
        const { post } = await adminCreateBlogPost(adminKey, {
          title:        draft.title.trim(),
          slug:         draft.slug.trim() || undefined,
          category:     draft.category.trim(),
          excerpt:      draft.excerpt.trim(),
          content:      draft.content,
          image:        draft.image.trim(),
          read_time:    draft.read_time.trim() || '5 perc olvasás',
          ...publish,
        });
        toast.success(publish.scheduled_at
          ? `Cikk ütemezve — ${new Date(publish.scheduled_at).toLocaleString('hu-HU')}`
          : (publish.published ? 'Cikk publikálva.' : 'Piszkozat elmentve.'));
        onSaved(post);
      } else {
        const { post } = await adminUpdateBlogPost(adminKey, draft.id, {
          title:        draft.title.trim(),
          slug:         draft.slug.trim(),
          category:     draft.category.trim(),
          excerpt:      draft.excerpt.trim(),
          content:      draft.content,
          image:        draft.image.trim(),
          read_time:    draft.read_time.trim() || '5 perc olvasás',
          ...publish,
        });
        toast.success('Változtatások mentve.');
        // A stíluspontszám minden mentésnél újraszámolódik — a publikálást
        // nem blokkoljuk, csak figyelmeztetünk, ha maradt piros jelzés.
        try {
          const report = await adminStyleCheck(adminKey, post.slug);
          if (report.red > 0) {
            toast.warning(
              `Mentve, de ${report.red} piros jelzés maradt (stíluspont: ${report.style_score}).`,
            );
          }
        } catch { /* a figyelmeztetés nem kritikus */ }
        onSaved(post);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mentési hiba.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    setDeleting(true);
    try {
      await adminDeleteBlogPost(adminKey, draft.id);
      toast.success('Cikk törölve.');
      onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Törlési hiba.');
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <Button
            data-testid="admin-blog-editor-back-btn"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-gray-300 hover:text-white"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Vissza a listához
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-blog-seo-check-btn"
              variant="ghost"
              size="sm"
              onClick={handleSeoCheck}
              disabled={loadingSeo}
              className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10"
            >
              {loadingSeo
                ? <><Loader2 className="mr-1.5 animate-spin" size={14} /> Elemzés…</>
                : <><SearchCheck size={14} className="mr-1.5" /> SEO ellenőrzés</>}
            </Button>
            <Button
              data-testid="admin-blog-preview-toggle"
              variant="ghost"
              size="sm"
              onClick={() => setShowPreview((v) => !v)}
              className="text-gray-300 hover:text-white"
            >
              {showPreview ? 'Szerkesztés' : 'Előnézet'}
            </Button>
            {!isNew && (
              <Button
                data-testid="admin-blog-delete-btn"
                variant="ghost"
                size="sm"
                onClick={() => setShowDelete(true)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <Trash2 size={14} className="mr-1.5" />
                Törlés
              </Button>
            )}
            <Button
              data-testid="admin-blog-save-btn"
              size="sm"
              disabled={!canSave || saving}
              onClick={handleSave}
              className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
            >
              {saving ? <><Loader2 className="mr-2 animate-spin" size={14} /> Mentés…</> : <><Save size={14} className="mr-1.5" /> {isNew ? 'Létrehozás' : 'Mentés'}</>}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {showPreview ? (
          <div data-testid="admin-blog-preview" className="max-w-4xl mx-auto">
            <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-yellow-500">
              <Eye size={14} /> Élő előnézet — így fog kinézni a cikk
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
              {/* Hero image — same as live BlogPostPage */}
              <div className="relative h-64 md:h-96 overflow-hidden">
                {draft.image ? (
                  <img src={draft.image} alt={draft.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-300" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/40 to-transparent" />
                <div className="absolute inset-0 flex items-end">
                  <div className="w-full px-6 md:px-10 pb-8 md:pb-10">
                    <div className="max-w-3xl text-white">
                      <div className="inline-flex items-center text-sm text-yellow-300 mb-4">
                        <ArrowLeft className="mr-1.5" size={14} />
                        Tudástár
                        <ChevronRight className="mx-1 opacity-60" size={14} />
                        <span className="text-white/80">{draft.category || 'Kategória'}</span>
                      </div>
                      <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {draft.title || <span className="text-white/50">Cím…</span>}
                      </h1>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-white/85">
                        <span className="inline-block bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full tracking-wider">
                          {draft.category || 'Kategória'}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Calendar size={14} />
                          {new Date(draft.date || Date.now()).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1.5"><Clock size={14} />{draft.read_time || '5 perc olvasás'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Body — same as live BlogPostPage */}
              <div className="px-6 md:px-10 pt-10 pb-12 text-gray-900">
                <div className="max-w-3xl mx-auto">
                  <p className="text-lg md:text-xl text-gray-600 italic border-l-4 border-yellow-600 pl-5 mb-10">
                    {draft.excerpt || <span className="text-gray-400">Bevezető…</span>}
                  </p>
                  <div className="prose-like">
                    {renderArticleContent(draft.content)}
                  </div>
                  <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>Írta:</span>
                      <strong className="text-gray-900">Orosz Gábor</strong>
                    </div>
                    <span className="inline-flex items-center border border-gray-300 rounded-md px-4 py-2 text-sm font-semibold text-gray-700">
                      <Share2 className="mr-2" size={16} />
                      Cikk megosztása
                    </span>
                  </div>
                  <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-8 md:p-10">
                    <h3 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Készen állsz a valódi átalakulásra?
                    </h3>
                    <p className="text-white/80 mb-5 leading-relaxed">
                      Foglalj egy ingyenes konzultációs időpontot, és nézzük át együtt, melyik programom illik a leginkább a céljaidhoz és az időbeosztásodhoz.
                    </p>
                    <span className="inline-flex items-center bg-yellow-600 text-gray-900 font-bold rounded-md px-4 py-2 text-sm">
                      Időpontfoglalás
                      <ChevronRight className="ml-1" size={18} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-5">
            {seoResult && (
              <div data-testid="admin-blog-seo-result" className="rounded-xl border border-emerald-700/40 bg-emerald-950/30 p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className={
                      'w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold border-2 ' +
                      (seoResult.score >= 80 ? 'border-emerald-500 text-emerald-300'
                        : seoResult.score >= 50 ? 'border-yellow-500 text-yellow-300'
                        : 'border-red-500 text-red-300')
                    }>
                      {seoResult.score}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-white">SEO pontszám</div>
                      <div className="text-xs text-gray-400">AI-elemzés: cím, meta, kulcsszavak, olvashatóság</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="admin-blog-seo-close"
                    onClick={() => setSeoResult(null)}
                    className="text-gray-500 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="space-y-2 mb-3">
                  {seoResult.checks.map((c, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm">
                      <span className={
                        'mt-0.5 shrink-0 w-2 h-2 rounded-full ' +
                        (c.status === 'ok' ? 'bg-emerald-400' : c.status === 'warn' ? 'bg-yellow-400' : 'bg-red-400')
                      } />
                      <div>
                        <span className="font-semibold text-white">{c.name}:</span>{' '}
                        <span className="text-gray-300">{c.detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {seoResult.suggestions?.length > 0 && (
                  <div className="border-t border-emerald-800/40 pt-3">
                    <div className="text-xs font-bold uppercase tracking-widest text-emerald-300 mb-1.5">Javaslatok</div>
                    <ul className="space-y-1 text-sm text-gray-300 list-disc list-inside">
                      {seoResult.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest flex items-center justify-between">
                <span>Cím</span>
                <button
                  type="button"
                  data-testid="admin-blog-ai-titles-btn"
                  onClick={handleGenTitles}
                  disabled={loadingTitles}
                  className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 disabled:opacity-60"
                >
                  {loadingTitles
                    ? <><Loader2 className="animate-spin" size={12} /> Generálás…</>
                    : <><Sparkles size={12} /> AI címjavaslatok</>}
                </button>
              </Label>
              <Input
                data-testid="admin-blog-title-input"
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder="A cikk címe"
                className="mt-1.5 bg-gray-900 border-gray-700 text-white"
              />
              {aiTitles.length > 0 && (
                <div data-testid="admin-blog-ai-titles-list" className="mt-2 rounded-lg border border-yellow-700/40 bg-yellow-950/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-yellow-400">
                      Kattints egy javaslatra a beillesztéshez
                    </span>
                    <button
                      type="button"
                      data-testid="admin-blog-ai-titles-close"
                      onClick={() => setAiTitles([])}
                      className="text-gray-500 hover:text-white"
                    >
                      <X size={13} />
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {aiTitles.map((t, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`admin-blog-ai-title-option-${i}`}
                        onClick={() => { set({ title: t }); setAiTitles([]); toast.success('Cím beillesztve.'); }}
                        className="w-full text-left text-sm text-gray-200 rounded-md border border-gray-700 bg-gray-900/70 hover:border-yellow-600 hover:bg-yellow-600/10 px-3 py-2 transition-colors"
                      >
                        {t}
                        <span className="ml-2 text-[10px] text-gray-500">{t.length} kar.</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Slug (URL)</Label>
                <Input
                  data-testid="admin-blog-slug-input"
                  value={draft.slug}
                  onChange={(e) => set({ slug: e.target.value })}
                  placeholder={isNew ? 'Automatikusan a címből (ha üresen hagyod)' : 'kalipper-utmutato'}
                  className="mt-1.5 bg-gray-900 border-gray-700 text-white font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Kategória</Label>
                <Select
                  value={newCategoryMode ? '__new__' : (categoryOptions.includes(draft.category) ? draft.category : '')}
                  onValueChange={(v) => {
                    if (v === '__new__') { setNewCategoryMode(true); set({ category: '' }); }
                    else { setNewCategoryMode(false); set({ category: v }); }
                  }}
                >
                  <SelectTrigger data-testid="admin-blog-category-select" className="mt-1.5 bg-gray-900 border-gray-700 text-white">
                    <SelectValue placeholder="Válassz kategóriát…" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    {categoryOptions.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`admin-blog-category-option-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__" data-testid="admin-blog-category-option-new" className="text-yellow-400">
                      + Új kategória…
                    </SelectItem>
                  </SelectContent>
                </Select>
                {newCategoryMode && (
                  <Input
                    data-testid="admin-blog-category-input"
                    value={draft.category}
                    onChange={(e) => set({ category: e.target.value })}
                    placeholder="Új kategória neve"
                    autoFocus
                    className="mt-2 bg-gray-900 border-gray-700 text-white"
                  />
                )}
              </div>
            </div>

            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest">Bevezető (excerpt)</Label>
              <Textarea
                data-testid="admin-blog-excerpt-input"
                value={draft.excerpt}
                onChange={(e) => set({ excerpt: e.target.value })}
                rows={3}
                placeholder="Rövid, 2-3 mondatos összefoglaló, ami a kártyán és a keresőben megjelenik."
                className="mt-1.5 bg-gray-900 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest flex items-center justify-between">
                <span>Tartalom (Markdown)</span>
                <span className="flex items-center gap-3 flex-wrap justify-end">
                  <AiScoreBadge
                    score={draft.ai_score ?? 0}
                    level={draft.ai_level || 'human'}
                    testId="admin-blog-editor-ai-score"
                  />
                  <button
                    type="button"
                    data-testid="admin-blog-inline-image-btn"
                    onClick={() => setInlineImagePickerOpen(true)}
                    className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300"
                  >
                    <ImagePlus size={12} /> Kép beszúrás
                  </button>
                  <button
                    type="button"
                    data-testid="admin-blog-ai-proofread-btn"
                    onClick={handleProofread}
                    disabled={loadingProofread}
                    className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-sky-400 hover:text-sky-300 disabled:opacity-60"
                  >
                    {loadingProofread
                      ? <><Loader2 className="animate-spin" size={12} /> Magyarosítás… (akár 1 perc)</>
                      : <><SpellCheck2 size={12} /> Magyarosítás + helyesírás</>}
                  </button>
                  <button
                    type="button"
                    data-testid="admin-blog-ai-humanize-btn"
                    onClick={handleHumanize}
                    disabled={loadingHumanize}
                    className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200 disabled:opacity-60"
                  >
                    {loadingHumanize
                      ? <><Loader2 className="animate-spin" size={12} /> Emberibbé tétel… (akár 2 perc)</>
                      : <><UserRound size={12} /> Emberibb szöveg</>}
                  </button>
                  {hasBackup && !isNew && (
                    <button
                      type="button"
                      data-testid="admin-blog-humanize-revert-btn"
                      onClick={handleRevertHumanize}
                      disabled={reverting}
                      className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-white disabled:opacity-60"
                    >
                      {reverting
                        ? <><Loader2 className="animate-spin" size={12} /> Visszaállítás…</>
                        : <><Undo2 size={12} /> Eredeti visszaállítása</>}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="admin-blog-ai-cleanup-btn"
                    onClick={handleCleanup}
                    disabled={loadingCleanup}
                    className="normal-case tracking-normal inline-flex items-center gap-1.5 text-xs font-semibold text-yellow-400 hover:text-yellow-300 disabled:opacity-60"
                  >
                    {loadingCleanup
                      ? <><Loader2 className="animate-spin" size={12} /> Tisztítás… (akár 1-2 perc)</>
                      : <><Wand2 size={12} /> AI tisztítás</>}
                  </button>
                  <span className="text-[10px] text-gray-500 normal-case tracking-normal">
                    ## fejléc • **félkövér** • • pont • ![alt](url) • [[illustration:chest]]
                  </span>
                </span>
              </Label>
              <div className="mt-1.5 grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4 items-start">
                <Textarea
                  ref={contentTextareaRef}
                  data-testid="admin-blog-content-input"
                  value={draft.content}
                  onChange={(e) => setContent(e.target.value)}
                  onPaste={handleContentPaste}
                  onSelect={rememberContentSelection}
                  onKeyUp={rememberContentSelection}
                  onClick={rememberContentSelection}
                  rows={22}
                  placeholder="## Első fejléc&#10;&#10;Első bekezdés **kiemeléssel**.&#10;&#10;• Első pont&#10;• Második pont"
                  className="bg-gray-900 border-gray-700 text-white font-mono text-sm leading-relaxed"
                />
                {!isNew && (
                  <div className="rounded-xl border border-gray-800 bg-gray-950/60 p-3 xl:max-h-[640px] xl:overflow-y-auto">
                    <StyleCheckPanel
                      adminKey={adminKey}
                      slug={draft.slug}
                      title={draft.title}
                      onApply={applyStyleFinding}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-widest">Fejléckép URL</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    data-testid="admin-blog-image-input"
                    value={draft.image}
                    onChange={(e) => set({ image: e.target.value })}
                    placeholder="https://…"
                    className="bg-gray-900 border-gray-700 text-white font-mono text-xs"
                  />
                  <BlogImagePicker
                    usedImages={usedImages}
                    currentImage={draft.image}
                    onSelect={(url) => set({ image: url })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-widest flex items-center justify-between">
                  <span>Olvasási idő</span>
                  <span className="text-[10px] text-gray-500 normal-case tracking-normal">
                    {readTimeTouched ? 'kézi' : 'automatikus a tartalomból'}
                  </span>
                </Label>
                <Input
                  data-testid="admin-blog-readtime-input"
                  value={draft.read_time}
                  onChange={(e) => { setReadTimeTouched(true); set({ read_time: e.target.value }); }}
                  placeholder="5 perc olvasás"
                  className="mt-1.5 bg-gray-900 border-gray-700 text-white"
                />
              </div>
            </div>

            <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
              <div className="mb-3">
                <div className="text-sm font-semibold text-white">Publikálási állapot</div>
                <div className="text-xs text-gray-500">
                  Piszkozat: rejtett. Ütemezett: automatikusan megjelenik a megadott időpontban. Publikálva: azonnal látható.
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { key: 'draft',     label: 'Piszkozat',  icon: EyeOff },
                  { key: 'scheduled', label: 'Ütemezés',   icon: CalendarClock },
                  { key: 'published', label: 'Publikálva', icon: Eye },
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`admin-blog-status-${key}`}
                    onClick={() => setStatus(key)}
                    className={
                      'flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold transition-all ' +
                      (status === key
                        ? 'border-yellow-600 bg-yellow-600/15 text-yellow-300'
                        : 'border-gray-700 bg-gray-800/60 text-gray-300 hover:border-gray-600 hover:text-white')
                    }
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>
              {status === 'scheduled' && (
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-widest">
                    Publikálás időpontja (helyi idő)
                  </Label>
                  <Input
                    data-testid="admin-blog-schedule-input"
                    type="datetime-local"
                    value={scheduleLocal}
                    onChange={(e) => setScheduleLocal(e.target.value)}
                    min={isoToLocalInput(new Date(Date.now() + 60_000).toISOString())}
                    className="mt-1.5 bg-gray-900 border-gray-700 text-white"
                  />
                  <p className="text-[11px] text-gray-500 mt-1.5">
                    A rendszer 60 másodpercenként ellenőrzi az ütemezéseket, és a megadott időpontban automatikusan publikálja a cikket.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <Dialog open={cleanupResult !== null} onOpenChange={(open) => { if (!open) setCleanupResult(null); }}>
        <DialogContent data-testid="admin-blog-cleanup-dialog" className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 size={18} className="text-yellow-600" />
              AI tisztítás — előnézet
            </DialogTitle>
            <DialogDescription>
              Az átfogalmazott, SEO-barát változat. Ha jóváhagyod, lecseréli a jelenlegi tartalmat.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 text-gray-900">
            <div className="prose-like">
              {renderArticleContent(cleanupResult || '')}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="admin-blog-cleanup-cancel"
              variant="outline"
              onClick={() => setCleanupResult(null)}
            >
              <X size={14} className="mr-1.5" /> Mégse
            </Button>
            <Button
              data-testid="admin-blog-cleanup-apply"
              onClick={() => {
                setContent(cleanupResult);
                setCleanupResult(null);
                toast.success('Tartalom lecserélve az AI-tisztított változatra.');
              }}
              className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
            >
              <Check size={14} className="mr-1.5" /> Beillesztés
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={proofreadResult !== null} onOpenChange={(open) => { if (!open) setProofreadResult(null); }}>
        <DialogContent data-testid="admin-blog-proofread-dialog" className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SpellCheck2 size={18} className="text-sky-600" />
              Magyarosítás + helyesírás — előnézet
            </DialogTitle>
            <DialogDescription>
              A cikk tartalma és struktúrája változatlan — csak a helyesírás, központozás, egybe/külön írás,
              névelők és a magyartalan mondatok lettek javítva. Ha jóváhagyod, lecseréli a jelenlegi tartalmat.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 text-gray-900">
            <div className="prose-like">
              {renderArticleContent(proofreadResult || '')}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="admin-blog-proofread-cancel"
              variant="outline"
              onClick={() => setProofreadResult(null)}
            >
              <X size={14} className="mr-1.5" /> Mégse
            </Button>
            <Button
              data-testid="admin-blog-proofread-apply"
              onClick={() => {
                setContent(proofreadResult);
                setProofreadResult(null);
                toast.success('Tartalom lecserélve a nyelvileg javított változatra.');
              }}
              className="bg-sky-600 hover:bg-sky-700 text-white font-bold"
            >
              <Check size={14} className="mr-1.5" /> Beillesztés
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={humanizeResult !== null} onOpenChange={(open) => { if (!open) setHumanizeResult(null); }}>
        <DialogContent data-testid="admin-blog-humanize-dialog" className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserRound size={18} className="text-emerald-600" />
              Emberibb szöveg — előnézet
            </DialogTitle>
            <DialogDescription>
              A sablonos AI-fordulatok, az egyenszabású mondatritmus és a túlzó kiemelések javítva;
              a tények, számok, linkek és a szerkezet változatlanok.
              {isNew
                ? ' Jóváhagyás után a szerkesztőbe kerül — a mentés külön lépés.'
                : ' Jóváhagyás után azonnal mentjük, az eredetit pedig megőrizzük.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 text-gray-900">
            {humanizeResult?.excerpt && (
              <div className="mb-5 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mb-1">
                  Új bevezető
                </div>
                <p className="text-sm text-gray-800" data-testid="admin-blog-humanize-excerpt">
                  {humanizeResult.excerpt}
                </p>
              </div>
            )}
            <div className="prose-like">
              {renderArticleContent(humanizeResult?.content || '')}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              data-testid="admin-blog-humanize-cancel"
              variant="outline"
              onClick={() => setHumanizeResult(null)}
            >
              <X size={14} className="mr-1.5" /> Mégse
            </Button>
            <Button
              data-testid="admin-blog-humanize-apply"
              onClick={applyHumanized}
              disabled={applyingHumanize}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
            >
              {applyingHumanize
                ? <><Loader2 className="mr-1.5 animate-spin" size={14} /> Mentés…</>
                : <><Check size={14} className="mr-1.5" /> {isNew ? 'Beillesztés' : 'Alkalmazás és mentés'}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={inlineImagePickerOpen} onOpenChange={setInlineImagePickerOpen}>
        <DialogContent data-testid="admin-blog-inline-image-dialog" className="bg-gray-900 border-gray-700 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kép beszúrása a szövegbe</DialogTitle>
            <DialogDescription className="text-gray-400">
              Kattints egy képre a kurzor jelenlegi helyére való beillesztéséhez. A képaláírást (alt szöveg)
              azonnal átírhatod, miután a kép beszúrásra került.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mb-3">
            {BLOG_IMAGE_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                data-testid={`inline-gallery-cat-${c}`}
                onClick={() => setInlineCat(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                  inlineCat === c ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {BLOG_IMAGE_GALLERY
              .filter((i) => inlineCat === 'Mind' || i.category === inlineCat)
              .map((img) => (
                <button
                  key={img.url}
                  type="button"
                  data-testid={`inline-gallery-img-${img.url.split('?')[0].slice(-20)}`}
                  onClick={() => insertInlineImage(img.url)}
                  className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-emerald-500 transition-all aspect-[16/10]"
                >
                  <img
                    src={img.url.replace('w=1200&h=750', 'w=400&h=250')}
                    alt={img.category}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <span className="absolute bottom-1.5 left-1.5 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-950/85 text-gray-100">
                    {img.category}
                  </span>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent data-testid="admin-blog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Biztosan törlöd?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{draft.title}&quot; véglegesen törlődik. Ez a művelet nem visszavonható.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-blog-delete-cancel">
              <X size={14} className="mr-1.5" /> Mégse
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-blog-delete-confirm-btn"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <><Loader2 className="mr-2 animate-spin" size={14} /> Törlés…</> : <><Trash2 size={14} className="mr-1.5" /> Törlés</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// -------------------------------------------------------------------------
// Page shell
// -------------------------------------------------------------------------
const AdminBlogPage = () => {
  useDocumentMeta({ title: 'Blog Szerkesztő | Orosz Coaching Admin' });
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, {} = new, {..post} = edit
  const [loadingPost, setLoadingPost] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [autoHumanize, setAutoHumanize] = useState(false);
  const loadListRef = useRef(null);
  const batch = useHumanizeBatch(adminKey, () => loadListRef.current?.());
  const style = useStyleSummaries(adminKey);

  const loadList = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const data = await adminListBlogPosts(adminKey);
      setPosts(data.posts || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Lejárt admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else {
        toast.error(err?.response?.data?.detail || 'Betöltési hiba.');
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { if (adminKey) loadList(); }, [adminKey, loadList]);
  useEffect(() => { loadListRef.current = loadList; }, [loadList]);

  // A 3 legrosszabb pontszámú cikk kiemelt jelölést kap.
  const worstSlugs = useMemo(() => [...posts]
    .sort((a, b) => (b.ai_score || 0) - (a.ai_score || 0))
    .filter((p) => (p.ai_score || 0) >= 30)
    .slice(0, 3)
    .map((p) => p.slug), [posts]);

  const movePost = async (id, dir) => {
    const idx = posts.findIndex((p) => p.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= posts.length) return;
    const next = [...posts];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPosts(next);
    setReordering(true);
    try {
      await adminReorderBlogPosts(adminKey, next.map((p) => p.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A sorrend mentése nem sikerült.');
      await loadList();
    } finally {
      setReordering(false);
    }
  };

  const openPost = async (postId, { humanize = false } = {}) => {
    setLoadingPost(true);
    try {
      const { post } = await adminGetBlogPost(adminKey, postId);
      setAutoHumanize(humanize);
      setEditing(post);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A cikk betöltése nem sikerült.');
    } finally {
      setLoadingPost(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setAdminKey('');
    setPosts([]);
    setEditing(null);
  };

  if (!adminKey) return <LoginScreen onAuth={setAdminKey} />;

  if (loadingPost) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="animate-spin text-yellow-500" size={32} />
      </div>
    );
  }

  if (editing !== null) {
    return (
      <PostEditor
        adminKey={adminKey}
        initialPost={editing.id ? editing : null}
        categories={[...new Set(posts.map((p) => p.category).filter(Boolean))]}
        usedImages={posts.filter((p) => p.id !== editing.id).map((p) => p.image).filter(Boolean)}
        autoHumanize={autoHumanize}
        onBack={() => { setEditing(null); setAutoHumanize(false); loadList(); style.reload(); }}
        onSaved={() => { setEditing(null); setAutoHumanize(false); loadList(); style.reload(); }}
        onDeleted={() => { setEditing(null); setAutoHumanize(false); loadList(); }}
      />
    );
  }

  return (
    <PostList
      posts={posts}
      loading={loading}
      onSelect={openPost}
      onHumanize={(id) => openPost(id, { humanize: true })}
      onCreate={() => setEditing({})}
      onRefresh={loadList}
      onLogout={handleLogout}
      onMove={movePost}
      reordering={reordering}
      batch={batch}
      worstSlugs={worstSlugs}
      styleRows={style.rows}
    />
  );
};

export default AdminBlogPage;
